import { PlusIcon, TrashIcon } from "@heroicons/react/24/solid";
import Button from "../Button/Button";
import "./versionManager.style.sass";
import { api } from "@src/utils/axios-instance";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useCharacterStore } from "@src/stores/characterStore";
import { useState } from "react";
import Modal from "../Modal/Modal";
import { Version } from "@src/types/global";

interface IProps {
    addVersionFn: () => void;
    removeVersionFn: () => void;
    version: Version;
}

/**
 * Displays buttons allowing the user to add a version or delete the current one. Requires confirmation upon deletion.
 * @param addVersionFn - The function to call when adding a version
 * @param removeVersionFn - The function to call when removing a version
 */

const VersionManager = ({ addVersionFn, removeVersionFn, version }: IProps) => {
    const [modalOpen, setModalOpen] = useState(false);
    const { setSelectedVersion } = useCharacterStore((state) => ({
        setSelectedVersion: state.setSelectedVersion,
    }));
    async function deleteVersion() {
        await api.delete(
            "/characters/" + version.character_id + "/version/" + version.id,
        );
    }
    const queryClient = useQueryClient();
    const deleteMutation = useMutation({
        mutationFn: deleteVersion,
        mutationKey: [
            "characters",
            version.character_id,
            "versions" + version.id,
        ],
        onSuccess: () => {
            toast.success("Version deleted");
            setSelectedVersion(0);
            setModalOpen(false);
            queryClient.setQueryData(
                ["characters", version.character_id, "versions" + version.id],
                (oldData: Version[]) => {
                    let cleanVersions = oldData.filter(
                        (v: Version) => v.id != version.id,
                    );
                    return cleanVersions;
                },
            );
        },
    });
    return (
        <div className="version__manager">
            <Button type="button" preset="stealth" fn={addVersionFn}>
                <PlusIcon className="icon" style={{ verticalAlign: "sub" }} />{" "}
                Add version
            </Button>

            <button
                aria-label={`Delete version ${version.version_name}`}
                onClick={() => setModalOpen(true)}
            >
                <TrashIcon className="icon" />{" "}
            </button>
            <Modal
                isOpen={modalOpen}
                closeModalFn={() => setModalOpen(false)}
                actions={
                    <>
                        <Button
                            preset="danger"
                            fn={() => {
                                if (version.id != null) {
                                    deleteMutation.mutate();
                                }
                                removeVersionFn();
                                setModalOpen(false);
                            }}
                            aria-label="Confirm delete"
                        >
                            Yes, delete
                        </Button>
                        <Button
                            preset="secondary"
                            fn={() => setModalOpen(false)}
                            aria-label="Cancel"
                        >
                            No, go back
                        </Button>
                    </>
                }
            >
                <h4>Delete version "{version.version_name}"?</h4>
                <p>
                    Are you sure you want to delete this version ? This cannot
                    be undone.
                </p>
            </Modal>
        </div>
    );
};
export default VersionManager;
